import type { MutableRefObject } from "react";
import api from "../../../api/api";
import { responseError } from "../../../api/apiError";
import { getColumnLetter, id, pascalToSentenceCase } from "../../utils";
import {
  fillCall,
  fillDrilldownCall,
  getFieldValue,
  refreshAllPivotTables,
  type Item,
  type ItemsState,
  type ParameterDefinition,
  type StringState,
} from "../ReportServices";

type ReportColumnDefinition = {
  field: string;
  type: string;
  name: string;
  filter: string;
  call: string;
};

type LoadReportDataOptions = {
  selectedDataDefinitionIndex: number;
  parameterDefinitions: ParameterDefinition[];
  dataDefinition: ParameterDefinition;
  dataDefinitions: ParameterDefinition[];
  selectedItem: StringState;
  items: ItemsState;
  selectedUdfs: string;
  udfs: Item[];
  authToken: string;
  identityProvider: string;
  pageSize: number;
  dataLoadCancelTokenRef: MutableRefObject<string>;
  dataLoadDurationRef: MutableRefObject<number>;
  saveParams: (context: Excel.RequestContext, selectedItem: StringState, saveHeader?: boolean) => Promise<void>;
  refreshAllQueriesLocal: () => Promise<void>;
  setPercent: (value: number) => void;
  setIsLoading: (value: boolean) => void;
  setCanCancel: (value: boolean) => void;
  setDataLoadDuration: (value: number) => void;
  setGotData: (value: boolean) => void;
  setError: (value: string) => void;
  onLoadingComplete?: () => void;
  dataSyncPageInterval?: number;
};

const DEFAULT_DATA_SYNC_PAGE_INTERVAL = 5;
const PERF_LOG_PREFIX = "  ** BMX PERF";

type PerfTimer = {
  label: string;
  startTime: number;
};

const formatDuration = (startTime: number): string => `${(performance.now() - startTime).toFixed(2)} ms`;

const logPerf = (message: string, details?: Record<string, unknown>) => {
  if (details) {
    console.log(`${PERF_LOG_PREFIX} ${message}`, details);
    return;
  }

  console.log(`${PERF_LOG_PREFIX} ${message}`);
};

const startPerfTimer = (label: string): PerfTimer => ({ label, startTime: performance.now() });

const stopPerfTimer = (timer: PerfTimer, details?: Record<string, unknown>): number => {
  const elapsedMs = performance.now() - timer.startTime;
  logPerf(`${timer.label}: ${elapsedMs.toFixed(2)} ms`, details);
  return elapsedMs;
};

const getReportEntityCode = (body: Record<string, unknown>, call: string, selectedDataDefinition: ParameterDefinition): string => {
  const candidateKeys = ["entityCode", "entity", "entityType", "apiEndpoint"];

  for (const key of candidateKeys) {
    const value = body[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  const endpoint = call.split("&")[0]?.trim();
  if (endpoint.length > 0) return endpoint;

  return selectedDataDefinition.name || "Unknown";
};

const getExcelErrorMessage = (error: unknown): string => {
  const officeError = error as OfficeExtension.Error & { debugInfo?: { errorLocation?: string } };
  const parts = [officeError?.code, officeError?.message || String(error)].filter(Boolean);
  const location = officeError?.debugInfo?.errorLocation;
  return location ? `${parts.join(": ")} at ${location}` : parts.join(": ");
};

const buildReportDataBody = (
  call: string,
  pageIndex: number,
  pageSize: number
): Record<string, unknown> => {
  let callParts = call.split("&");
  callParts = callParts.map((part) => part.replace(/%26/g, "&"));

  const callEndpoint = callParts[0];
  callParts = callParts.slice(1);

  const body: Record<string, unknown> = {
    apiEndpoint: callEndpoint,
    pageIndex,
    pageSize,
  };

  for (const part of callParts) {
    const eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      const key = part.substring(0, eqIndex);
      const value = part.substring(eqIndex + 1);
      body[key] = value;
    } else {
      body[part] = true;
    }
  }

  return body;
};

const readReportColumns = async (
  context: Excel.RequestContext,
  selectedDataDefinition: ParameterDefinition,
  selectedUdfs: string,
  udfs: Item[]
): Promise<ReportColumnDefinition[]> => {
  const specificSheetName = "(columns-" + id(selectedDataDefinition.name) + ")";
  const specificSheet = context.workbook.worksheets.getItemOrNullObject(specificSheetName);
  await context.sync();

  const sheet = !specificSheet.isNullObject ? specificSheet : context.workbook.worksheets.getItem("(columns)");
  const range = sheet.getUsedRange();
  await context.sync();
  range.load("values");
  await context.sync();

  const columns: ReportColumnDefinition[] = range.values
    .slice(1)
    .map((row: any) => ({ field: row[0], type: row[1], name: row[2], filter: row[3], call: row[4] }));

  if (columns.length === 0) {
    columns.push({ field: "id", type: "@", name: "ID", filter: "", call: "" });
  }

  if (selectedUdfs) {
    const udfFields = selectedUdfs.split(", ");
    for (const udfField of udfFields) {
      if (!columns.find((column) => column.field === udfField)) {
        columns.push({ field: udfField, type: "@", name: udfs.find((udf) => udf.id === udfField)?.name || udfField, filter: "", call: "" });
      }
    }
  }

  return columns;
};

const prepareDataSheet = async (
  context: Excel.RequestContext,
  selectedDataDefinition: ParameterDefinition
) => {
  const specificNameId = "data-" + id(selectedDataDefinition.name);
  const specificSheetName = "(" + specificNameId + ")";
  let specificSheet = context.workbook.worksheets.getItemOrNullObject(specificSheetName);
  await context.sync();

  const dataNameId = !specificSheet.isNullObject ? id(specificNameId) : "data";
  const sheetName = !specificSheet.isNullObject ? specificSheetName : "(data)";
  let sheet = !specificSheet.isNullObject ? specificSheet : context.workbook.worksheets.getItemOrNullObject("(data)");

  await context.sync();
  if (sheet.isNullObject) {
    sheet = context.workbook.worksheets.add(sheetName);
    await context.sync();
  } else {
    sheet.load("visibility");
    await context.sync();

    if (sheet.visibility !== "Visible") {
      sheet.visibility = "Visible";
      await context.sync();
    }

    try {
      sheet.protection.unprotect();
      await context.sync();
    } catch {
      sheet.delete();
      await context.sync();

      sheet = context.workbook.worksheets.add(sheetName);
      await context.sync();
    }
  }

  const dataUsedRange = sheet.getUsedRangeOrNullObject();
  await context.sync();
  if (!dataUsedRange.isNullObject) {
    dataUsedRange.clear();
    await context.sync();
  }

  return { sheet, sheetName, dataNameId };
};

const writeReportHeaders = async (
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  sheetName: string,
  dataNameId: string,
  columns: ReportColumnDefinition[]
) => {
  const headerValues = columns.map((column) => (column.type === "drilldown" ? "" : column.name) || pascalToSentenceCase(column.field));
  const range = sheet.getRange(`A1:${getColumnLetter(columns.length)}1`);
  range.numberFormat = Array(1).fill(columns.map(() => "@"));
  range.values = [headerValues];

  let dataName = context.workbook.names.getItemOrNullObject(dataNameId);
  await context.sync();
  if (dataName.isNullObject) {
    dataName = context.workbook.names.getItem("data");
  }
  dataName.formula = `='${sheetName}'!$A$1:${getColumnLetter(columns.length)}$1`;
  await context.sync();
};

const activateUserSheet = async (context: Excel.RequestContext, selectedDataDefinition: ParameterDefinition) => {
  const userSheet = context.workbook.worksheets.getItemOrNullObject(selectedDataDefinition.name);
  await context.sync();
  if (!userSheet.isNullObject) {
    userSheet.activate();
  } else {
    context.workbook.worksheets.getFirst().activate();
  }
  await context.sync();
};

const updateDataNamedRange = async (
  context: Excel.RequestContext,
  sheetName: string,
  dataNameId: string,
  columnCount: number,
  rowIndex: number
) => {
  let dataName = context.workbook.names.getItemOrNullObject(dataNameId);
  await context.sync();
  if (dataName.isNullObject) {
    dataName = context.workbook.names.getItem("data");
  }
  dataName.formula = `='${sheetName}'!$A$1:${getColumnLetter(columnCount)}${rowIndex}`;
};

const writeReportRows = async (
  sheet: Excel.Worksheet,
  sheetName: string,
  dataNameId: string,
  columns: ReportColumnDefinition[],
  rows: any[],
  rowIndex: number,
  selectedItem: StringState,
  pageIndex: number,
  syncPageInterval = DEFAULT_DATA_SYNC_PAGE_INTERVAL
): Promise<number> => {
  const rowValues = rows.map((row: any) => columns.map((column) =>
    column.type === "drilldown" ? `="`
      + (column.name.length > 0 ? column.name : "Drill down")
      + `"&T(N("#/drilldown?f=` + column.filter + `&c=` + fillDrilldownCall(column.call, row, selectedItem) + `"))`:
    getFieldValue(row, column.field) ?? ""));
  const endRowIndex = rowIndex + rows.length;

  if (rows.length > 0) {
    const range = sheet.getRange(`A${rowIndex + 1}:${getColumnLetter(columns.length)}${endRowIndex}`);
    range.numberFormat = Array(rowValues.length).fill(columns.map((column) => (
      column.type === "drilldown" ? "" :
      column.type?.length > 0 ? column.type : "@")));
    range.formulas = rowValues;

    if (syncPageInterval > 0 && (pageIndex + 1) % syncPageInterval === 0) {
      await updateDataNamedRange(sheet.context, sheetName, dataNameId, columns.length, endRowIndex);
      await sheet.context.sync();
    }
  }

  return endRowIndex;
};

const updateDataNamedRanges = async (
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  sheetName: string,
  dataNameId: string,
  columns: ReportColumnDefinition[],
  rowIndex: number
) => {
  let dataName = context.workbook.names.getItemOrNullObject(dataNameId);
  const columnNamesToCreate: { name: string; formula: string }[] = [];
  const columnNameObjsToDelete: { name: string; obj: Excel.NamedItem }[] = [];

  for (let i = 0; i < columns.length; i++) {
    const columnLetter = getColumnLetter(i + 1);
    let columnName = columns[i].name || columns[i].field || `Column${i + 1}`;
    columnName = columnName.replace(/\s+/g, "_").replace(/[^A-Za-z0-9_]/g, "");
    const columnRangeName = dataNameId + `_${columnName}`;
    columnNamesToCreate.push({
      name: columnRangeName,
      formula: `='${sheetName}'!$${columnLetter}$1:$${columnLetter}${rowIndex}`,
    });
    columnNameObjsToDelete.push({
      name: columnRangeName,
      obj: context.workbook.names.getItemOrNullObject(columnRangeName),
    });
  }

  await context.sync();

  if (dataName.isNullObject) {
    dataName = context.workbook.names.getItem("data");
  }
  dataName.formula = `='${sheetName}'!$A$1:${getColumnLetter(columns.length)}${rowIndex}`;

  for (const item of columnNameObjsToDelete) {
    try {
      if (!item.obj.isNullObject) {
        item.obj.delete();
      }
    } catch {
      // Name does not exist, continue.
    }
  }

  for (const columnDefinition of columnNamesToCreate) {
    try {
      context.workbook.names.add(columnDefinition.name, columnDefinition.formula);
    } catch (error) {
      console.error(`Error creating named range for column ${columnDefinition.name}:`, error);
    }
  }

  sheet.protection.protect();
  sheet.visibility = "Hidden";
  await context.sync();
};

const updateProcessingDuration = async (
  context: Excel.RequestContext,
  parameterDefinitions: ParameterDefinition[],
  selectedUdfs: string,
  dataLoadDuration: number
) => {
  try {
    const paramSheet = context.workbook.worksheets.getItem("(parameters)");
    paramSheet.protection.unprotect();
    await context.sync();

    const parameterValuesLength = parameterDefinitions.length + 1 + ((selectedUdfs?.length ?? 0) > 0 ? 1 : 0);
    const durationRow = 4 + parameterValuesLength;
    paramSheet.getRange(`A${durationRow}:B${durationRow}`).values = [["Processing:", dataLoadDuration.toFixed(2) + " seconds"]];
    paramSheet.protection.protect();
    await context.sync();
  } catch (error) {
    console.warn("Could not update processing duration:", error);
  }
};

const updateSheetVisibility = async (
  context: Excel.RequestContext,
  selectedItem: StringState
) => {
  const sheetsSheet = context.workbook.worksheets.getItemOrNullObject("(sheets)");
  const visibilityUpdates: { sheet: Excel.Worksheet; visibility: string }[] = [];

  await context.sync();
  if (sheetsSheet.isNullObject) return;

  const range = sheetsSheet.getUsedRange();
  range.load("values");
  await context.sync();

  const values = range.values.slice(1).map((row: any) => ({ sheet: row[0], parameter: row[1], condition: row[2] }));
  for (const value of values) {
    const visibleSheet = context.workbook.worksheets.getItemOrNullObject(value.sheet);
    visibilityUpdates.push({
      sheet: visibleSheet,
      visibility: selectedItem[value.parameter] === value.condition ? "Visible" : "Hidden",
    });
  }

  await context.sync();

  for (const update of visibilityUpdates) {
    if (!update.sheet.isNullObject) {
      update.sheet.visibility = update.visibility as Excel.SheetVisibility;
    }
  }
  await context.sync();
};

export const loadReportData = async (context: Excel.RequestContext, options: LoadReportDataOptions) => {
  const {
    selectedDataDefinitionIndex,
    parameterDefinitions,
    dataDefinition,
    dataDefinitions,
    selectedItem,
    items,
    selectedUdfs,
    udfs,
    authToken,
    identityProvider,
    pageSize,
    dataLoadCancelTokenRef,
    dataLoadDurationRef,
    saveParams,
    refreshAllQueriesLocal,
    setPercent,
    setIsLoading,
    setCanCancel,
    setDataLoadDuration,
    setGotData,
    setError,
    onLoadingComplete,
    dataSyncPageInterval = DEFAULT_DATA_SYNC_PAGE_INTERVAL,
  } = options;

  let dataLoadStep = "saving parameters";
  let loadingCompleted = false;
  const completeLoading = () => {
    if (loadingCompleted) return;

    loadingCompleted = true;
    setPercent(0);
    setIsLoading(false);
    setCanCancel(false);
    onLoadingComplete?.();
  };

  const currentCancelToken = Date.now().toString();
  dataLoadCancelTokenRef.current = currentCancelToken;

  setPercent(0);
  setIsLoading(true);
  setCanCancel(true);

  dataLoadDurationRef.current = 0;
  const startTime = performance.now();

  await saveParams(context, selectedItem, true);
  logPerf("Starting report data load", {
    dataDefinition: dataDefinitions[selectedDataDefinitionIndex]?.name || dataDefinition.name || "Data",
    pageSize,
  });

  try {
    dataLoadStep = "clearing sample data";
    const sampleName = context.workbook.names.getItemOrNullObject("sample");
    await context.sync();
    if (!sampleName.isNullObject) {
      sampleName.getRange().clear();
      sampleName.delete();
      setGotData(true);
    }

    dataLoadStep = "reading column definitions";
    const columns = await readReportColumns(context, dataDefinitions[selectedDataDefinitionIndex], selectedUdfs, udfs);

    dataLoadStep = "making data sheet writable";
    const { sheet, sheetName, dataNameId } = await prepareDataSheet(context, dataDefinitions[selectedDataDefinitionIndex]);

    dataLoadStep = "writing data headers";
    await writeReportHeaders(context, sheet, sheetName, dataNameId, columns);
    await activateUserSheet(context, dataDefinitions[selectedDataDefinitionIndex]);

    let rowIndex = 1;
    let pageIndex = 0;
    let totalCount = 0;

    do {
      if (dataLoadCancelTokenRef.current !== currentCancelToken) {
        console.log("Data load cancelled by user");
        break;
      }

      const call = fillCall(dataDefinitions[selectedDataDefinitionIndex].call, selectedItem, selectedUdfs, items);
      const body = buildReportDataBody(call, pageIndex, pageSize);
      const entityCode = getReportEntityCode(body, call, dataDefinitions[selectedDataDefinitionIndex]);
      logPerf(`Downloading entity code ${entityCode}`, { pageIndex, pageSize });

      dataLoadStep = "fetching report data";
      const downloadTimer = startPerfTimer(`Server roundtrip for entity code ${entityCode}`);
      const response = await api.post("report/data", body, authToken, identityProvider);
      if (!response.ok) throw await responseError(response);

      if (dataLoadCancelTokenRef.current !== currentCancelToken) {
        console.log("Data load cancelled by user");
        break;
      }

      const data: any = await response.json();
      const rows = data.rows;
      totalCount = data.totalCount ?? 0;
      stopPerfTimer(downloadTimer, { pageIndex, rows: rows.length, totalCount });

      if (dataDefinition.default === "Test") {
        console.log(rows.length > 0 ? Object.keys(rows[0]).join("\n") : []);
        console.log(rows);
      }

      dataLoadStep = "writing report data rows";
      const writeRowsTimer = startPerfTimer(`Populate worksheet ${sheetName} rows for entity code ${entityCode}`);
      rowIndex = await writeReportRows(sheet, sheetName, dataNameId, columns, rows, rowIndex, selectedItem, pageIndex, dataSyncPageInterval);
      stopPerfTimer(writeRowsTimer, { pageIndex, rows: rows.length, rowIndex });
      console.log(rows.length + " rows loaded, total count: " + totalCount);
      if (rows.length === 0) break;

      pageIndex++;
      if (totalCount > 0) {
        setPercent(Math.min(100, ((pageIndex * pageSize) / totalCount) * 100));
      }
      const elapsed = (performance.now() - startTime) / 1000;
      dataLoadDurationRef.current = elapsed;
      setDataLoadDuration(elapsed);

      if (rows.length >= totalCount) break;
    } while (totalCount === 0 || pageIndex * pageSize < totalCount);

    dataLoadStep = "updating data named ranges";
    const finalizeDataSheetTimer = startPerfTimer(`Finalize worksheet ${sheetName}`);
    await updateDataNamedRanges(context, sheet, sheetName, dataNameId, columns, rowIndex);
    stopPerfTimer(finalizeDataSheetTimer, { rows: Math.max(0, rowIndex - 1), columns: columns.length });

    const processingDurationTimer = startPerfTimer("Populate worksheet (parameters) processing duration");
    await updateProcessingDuration(context, parameterDefinitions, selectedUdfs, dataLoadDurationRef.current);
    stopPerfTimer(processingDurationTimer);

    if (dataLoadCancelTokenRef.current !== currentCancelToken) {
      console.log("Data load cancelled by user");
      return;
    }

    setGotData(true);
    completeLoading();

    dataLoadStep = "updating sheet visibility";
    const sheetVisibilityTimer = startPerfTimer("Update worksheet visibility");
    await updateSheetVisibility(context, selectedItem);
    stopPerfTimer(sheetVisibilityTimer);

    if (rowIndex <= 1) {
      setGotData(true);
      return;
    }

    dataLoadStep = "refreshing pivot tables";
    const pivotTimer = startPerfTimer("Refresh pivot tables");
    await refreshAllPivotTables();
    stopPerfTimer(pivotTimer);
    dataLoadStep = "refreshing queries";
    const queriesTimer = startPerfTimer("Refresh workbook queries");
    await refreshAllQueriesLocal();
    stopPerfTimer(queriesTimer);

    setGotData(true);
  } catch (error) {
    const errMsg = getExcelErrorMessage(error);
    if (errMsg.includes("payload size")) {
      setError("Cannot load data. Please select fewer dependency items.");
    } else {
      setError(`Error fetching data while ${dataLoadStep}: ${errMsg}`);
    }
  } finally {
    logPerf(`Report data load total elapsed: ${formatDuration(startTime)}`);
    completeLoading();
  }
};
